import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * HeatmapChart - A modern heatmap component for React 19
 * Replaces react-heatmap-grid with a pure CSS/React implementation
 */
function HeatmapChart({ data, handlechangea, _Index }) {
    // State
    const [matrixData, setMatrixData] = useState([]);
    const [xLabels, setXLabels] = useState([]);
    const [yLabels, setYLabels] = useState([]);
    const [isLoading, setIsLoading] = useState(!!(data?.RequestJson && data?.url));
    const [error, setError] = useState(null);

    // Refs
    const abortControllerRef = useRef(null);
    const prevRequestJsonRef = useRef(null);

    // Handle API response
    const handleResponse = useCallback((response) => {
        return response.text().then(text => {
            try {
                return text && JSON.parse(text);
            } catch {
                return null;
            }
        });
    }, []);

    // Fetch data
    const fetchData = useCallback(async () => {
        if (!data?.RequestJson || !data?.url) return;

        // Cancel any previous pending request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch(data.url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'requestHeader': data.RequestJson1,
                    'X-Browser-Url': window.location.href,
                },
                body: data.RequestJson,
                signal: controller.signal,
            });

            const apidata = await handleResponse(response);

            if (controller.signal.aborted) return;

            if (apidata?.dataset) {
                setMatrixData(JSON.parse(apidata.dataset));
                setXLabels(apidata.xLabels || []);
                setYLabels(apidata.yLabels || []);
            }

            setIsLoading(false);
        } catch (err) {
            if (err.name === 'AbortError') return;
            setError('Error loading data');
            setIsLoading(false);
        }
    }, [data?.url, data?.RequestJson, data?.RequestJson1, handleResponse]);

    // Fetch data when RequestJson changes
    useEffect(() => {
        if (data?.type === 'Heatmap' && data?.RequestJson && data?.url &&
            data.RequestJson !== prevRequestJsonRef.current) {
            prevRequestJsonRef.current = data.RequestJson;
            fetchData();
        }
    }, [data?.type, data?.RequestJson, data?.url, fetchData]);

    // Initial fetch
    useEffect(() => {
        if (data?.type === 'Heatmap' && data?.RequestJson && data?.url) {
            fetchData();
        }

        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [data?.type, data?.RequestJson, data?.url, fetchData]);

    // Calculate min and max values for color intensity
    const { minValue, maxValue } = useMemo(() => {
        if (!matrixData?.length) return { minValue: 0, maxValue: 100 };
        
        let min = Infinity;
        let max = -Infinity;
        
        matrixData.forEach(row => {
            row.forEach(value => {
                if (value < min) min = value;
                if (value > max) max = value;
            });
        });
        
        return { minValue: min, maxValue: max };
    }, [matrixData]);

    // Get cell background color based on value
    const getCellColor = useCallback((value) => {
        const intensity = maxValue === minValue 
            ? 0.5 
            : (value - minValue) / (maxValue - minValue);
        // Using primary color (teal-ish) with varying opacity
        return `rgba(42, 82, 75, ${intensity * 0.9 + 0.1})`;
    }, [minValue, maxValue]);

    // Get cell text color based on intensity
    const getTextColor = useCallback((value) => {
        const intensity = maxValue === minValue 
            ? 0.5 
            : (value - minValue) / (maxValue - minValue);
        return intensity > 0.5 ? '#ffffff' : '#1d252d';
    }, [minValue, maxValue]);

    const hasData = matrixData?.length > 0 && xLabels?.length > 0 && yLabels?.length > 0;

    return (
        <Card className="h-full flex flex-col">
            <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold text-foreground">
                    {data?.title}
                </CardTitle>
                {data?.Subtitle && (
                    <CardDescription className="text-sm text-muted-foreground">
                        {data.Subtitle}
                    </CardDescription>
                )}
            </CardHeader>

            <CardContent className="flex-1 pt-0 min-h-[300px]">
                {isLoading ? (
                    <div className="flex items-center justify-center h-full">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-full text-destructive">
                        <p className="text-sm">{error}</p>
                    </div>
                ) : !hasData ? (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                        <p className="text-sm">No data available</p>
                    </div>
                ) : (
                    <div className="overflow-auto">
                        <table className="border-collapse w-full">
                            <thead>
                                <tr>
                                    <th className="p-1 text-xs font-medium text-muted-foreground text-right pr-2 w-8">
                                        {/* Empty corner cell */}
                                    </th>
                                    {xLabels.map((label, idx) => (
                                        <th 
                                            key={idx}
                                            className="p-1 text-xs font-medium text-muted-foreground text-center min-w-[32px]"
                                        >
                                            {label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {matrixData.map((row, rowIdx) => (
                                    <tr key={rowIdx}>
                                        <td className="p-1 text-xs font-medium text-muted-foreground text-right pr-2 whitespace-nowrap">
                                            {yLabels[rowIdx]}
                                        </td>
                                        {row.map((value, colIdx) => (
                                            <td
                                                key={colIdx}
                                                className="p-0"
                                            >
                                <div
                                    className="flex items-center justify-center text-xs font-normal transition-colors h-6 min-w-8"
                                    style={{
                                        backgroundColor: getCellColor(value),
                                        color: getTextColor(value),
                                    }}
                                                    title={`${yLabels[rowIdx]} - ${xLabels[colIdx]}: ${value}`}
                                                >
                                                    {value}
                                                </div>
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export default HeatmapChart;

